import type { SearchProduct } from "openapi/shop_agent/client/types.gen";
import { brandSearchFailure } from "@/lib/brand-search-state";

export const BRAND_CATEGORY_ALL = "all";

export interface BridgeBrandTopCategory {
  category_name: string;
  category_level: number;
}

interface BridgeBrandTopCategoriesResponse {
  brand_name: string;
  top_categories: BridgeBrandTopCategory[];
}

interface BridgeFeaturedProductsResponse {
  products: SearchProduct[];
}

interface BridgeSimilarBrandResult {
  brand_name?: string;
  brand_slug?: string;
  directory_subheading?: string;
  brand_description?: string;
}

interface BridgeSimilarBrandsResponse {
  results: BridgeSimilarBrandResult[];
}

async function fetchBridgeJson<T>(url: string): Promise<T> {
  const response = await fetch(url, { method: "GET" });
  if (!response.ok) {
    throw new Error(`Bridge request failed: ${response.status} ${response.statusText}`);
  }
  return (await response.json()) as T;
}

export async function fetchBrandTopCategoriesViaBridge(
  brandName: string,
  minLevel: number = 2,
  limit: number = 3
): Promise<BridgeBrandTopCategoriesResponse> {
  const encodedBrand = encodeURIComponent(brandName);
  const params = new URLSearchParams({
    min_level: String(minLevel),
    limit: String(limit),
  });
  return fetchBridgeJson<BridgeBrandTopCategoriesResponse>(
    `/api/brands/${encodedBrand}/top-categories?${params.toString()}`
  );
}

/**
 * A brand-grid browse through the featured-products bridge: the brand's
 * products, within one category tab when `categoryName` is set.
 */
export async function fetchFeaturedProductsViaBridge(params: {
  brandName: string;
  categoryName?: string;
  categoryLevel?: number;
  limit?: number;
}): Promise<SearchProduct[]> {
  const encodedBrand = encodeURIComponent(params.brandName);
  const query = new URLSearchParams();
  const categoryName = params.categoryName;
  const categoryLevel = params.categoryLevel;
  if (categoryName && categoryName !== BRAND_CATEGORY_ALL && typeof categoryLevel === "number") {
    query.set("category_name", categoryName);
    query.set("category_level", String(categoryLevel));
  }
  query.set("limit", String(params.limit ?? 8));
  const suffix = query.toString();
  const url = `/api/brands/${encodedBrand}/featured-products${suffix ? `?${suffix}` : ""}`;
  const data = await fetchBridgeJson<BridgeFeaturedProductsResponse>(url);
  if (!Array.isArray(data.products)) throw new Error("Invalid featured products response");
  return data.products;
}

export async function fetchSimilarBrandsViaBridge(
  brandName: string,
  limit: number = 5
): Promise<BridgeSimilarBrandResult[]> {
  const encodedBrand = encodeURIComponent(brandName);
  // Keep pre-migration native responses out of the customer-backed cache key.
  const params = new URLSearchParams({ limit: String(limit), contract: "platform-v1" });
  const response = await fetch(`/api/brands/${encodedBrand}/similar-brands?${params.toString()}`, {
    method: "GET",
  });
  if (!response.ok) throw brandSearchFailure(response);
  const data = (await response.json()) as BridgeSimilarBrandsResponse;
  if (!Array.isArray(data.results)) throw brandSearchFailure();
  return data.results;
}
